"use client"

import type React from "react"
import { useState } from "react"
import { Button } from "@/shared/components/ui/button"
import { Card } from "@/shared/components/ui/card"
import { CheckCircle2, Mail, Phone, User, MessageSquare } from "lucide-react"

export function CTASection() {
  const [showForm, setShowForm] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget

    fetch("https://formsubmit.co/ajax/soporte@rapyfact.com", {
      method: "POST",
      body: new FormData(form),
    })
      .then((res) => res.ok && setIsSubmitted(true))
      .catch(() => alert("Hubo un error al enviar el mensaje. Inténtalo nuevamente."))
  }

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      "¡Hola! 👋 Estoy interesado en una cotización o más información sobre Rapyfact."
    )
    window.open(`https://wa.me/573135909091?text=${message}`, "_blank")
  }

  return (
    <section className="py-20 lg:py-32">
      <div className="container mx-auto px-4 lg:px-8">
        <Card className="relative overflow-hidden bg-gradient-to-br from-primary to-primary/80 p-12 lg:p-20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />

          <div className="relative z-10 text-center max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl lg:text-5xl font-bold text-white text-balance">
              Solicite una cotización hoy
            </h2>
            <p className="text-lg lg:text-xl text-white/90 text-pretty">
              Contáctenos
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="secondary"
                className="bg-white text-primary hover:bg-white/90"
                onClick={() => {
                  setShowForm(!showForm)
                  setIsSubmitted(false)
                }}
              >
                Comenzar Ahora
              </Button>

              {/* ✅ BOTÓN DE WHATSAPP FUNCIONAL */}
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 bg-transparent"
                onClick={handleWhatsApp}
              >
                Contactar Ventas
              </Button>
            </div>

            {showForm && (
              <div className="mt-10 bg-white/95 backdrop-blur-md p-8 lg:p-10 rounded-2xl text-left shadow-2xl border border-white/30">
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Anti-spam */}
                    <input type="hidden" name="_captcha" value="false" />
                    <input
                      type="hidden"
                      name="_subject"
                      value="Nueva solicitud de cotización - Rapyfact"
                    />
                    <input type="text" name="_honey" style={{ display: "none" }} />

                    <div className="space-y-2">
                      <label className="block text-primary text-sm font-semibold">
                        Nombre completo
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary/60" />
                        <input
                          type="text"
                          name="Nombre"
                          required
                          className="w-full rounded-lg pl-11 pr-4 py-3 bg-white border-2 border-primary/20 text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                          placeholder="Tu nombre completo"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-primary text-sm font-semibold">
                        Correo electrónico
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary/60" />
                        <input
                          type="email"
                          name="Correo"
                          required
                          className="w-full rounded-lg pl-11 pr-4 py-3 bg-white border-2 border-primary/20 text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                          placeholder="tu@correo.com"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-primary text-sm font-semibold">
                        Teléfono
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary/60" />
                        <input
                          type="tel"
                          name="Teléfono"
                          className="w-full rounded-lg pl-11 pr-4 py-3 bg-white border-2 border-primary/20 text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                          placeholder="+57 300 000 0000"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-primary text-sm font-semibold">
                        Mensaje
                      </label>
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-primary/60" />
                        <textarea
                          name="Mensaje"
                          rows={4}
                          className="w-full rounded-lg pl-11 pr-4 py-3 bg-white border-2 border-primary/20 text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                          placeholder="Escribe tu mensaje aquí..."
                          required
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="bg-primary text-primary-foreground hover:bg-primary/90 w-full shadow-lg hover:shadow-xl transition-all"
                    >
                      Enviar Solicitud
                    </Button>
                  </form>
                ) : (
                  <div className="text-center py-10 flex flex-col items-center justify-center">
                    <div className="bg-green-100 rounded-full p-4 mb-4">
                      <CheckCircle2 className="w-12 h-12 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">
                      ¡Gracias por tu mensaje!
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-md">
                      Hemos recibido tu solicitud y nuestro equipo se comunicará contigo pronto.
                    </p>
                    <Button
                      onClick={() => {
                        setIsSubmitted(false)
                        setShowForm(false)
                      }}
                      variant="outline"
                      size="lg"
                      className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    >
                      Cerrar
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    </section>
  )
}
